import * as Clipboard from 'expo-clipboard';
import React, { useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { BoardRows } from '@/components/predictions/BoardRows';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Segmented } from '@/components/ui/Segmented';
import { Skeleton } from '@/components/ui/Skeleton';
import { TextField } from '@/components/ui/TextField';
import {
  useCreatePredictionLeague,
  useJoinPredictionLeague,
  useLeavePredictionLeague,
  useMyPredictionLeagues,
  usePredictionLeaderboard,
  usePredictionLeagueBoard,
} from '@/features/predictions/game';
import { useAuth } from '@/lib/auth/AuthProvider';
import { errorKey } from '@/lib/utils/errors';

export default function PredictionLeaderboardScreen() {
  const { t } = useTranslation();
  const { userId } = useAuth();
  const [tab, setTab] = useState<'overall' | 'groups'>('overall');
  const [groupName, setGroupName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [formMessage, setFormMessage] = useState<string | null>(null);
  const [createdCode, setCreatedCode] = useState<string | null>(null);
  const [openLeagueId, setOpenLeagueId] = useState<string | null>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);

  const overallQuery = usePredictionLeaderboard();
  const leaguesQuery = useMyPredictionLeagues();
  const leagueBoard = usePredictionLeagueBoard(openLeagueId ?? undefined);
  const createLeague = useCreatePredictionLeague();
  const joinLeague = useJoinPredictionLeague();
  const leaveLeague = useLeavePredictionLeague();

  const onCreate = () => {
    setFormMessage(null);
    setCreatedCode(null);
    createLeague.mutate(groupName.trim(), {
      onSuccess: (league) => {
        setCreatedCode(league.invite_code);
        setGroupName('');
      },
      onError: (error) => setFormMessage(t(errorKey(error))),
    });
  };

  const onJoin = () => {
    setFormMessage(null);
    joinLeague.mutate(inviteCode.trim().toUpperCase(), {
      onSuccess: () => setInviteCode(''),
      onError: (error) => setFormMessage(t(errorKey(error))),
    });
  };

  const openLeague = (leaguesQuery.data ?? []).find((league) => league.id === openLeagueId);

  return (
    <Screen>
      <BackHeader title={t('predictions.leaderboard')} />
      <View className="pb-4">
        <Segmented
          options={[
            { value: 'overall', label: t('predictions.overall') },
            { value: 'groups', label: t('predictions.groups') },
          ]}
          value={tab}
          onChange={(next) => {
            setTab(next);
            setOpenLeagueId(null);
          }}
        />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 48 }}>
        {tab === 'overall' ? (
          overallQuery.isPending ? (
            <View className="gap-3 px-4">
              {[0, 1, 2, 3].map((index) => (
                <Skeleton key={index} className="h-14 w-full" />
              ))}
            </View>
          ) : overallQuery.isError ? (
            <ErrorState error={overallQuery.error} onRetry={() => overallQuery.refetch()} />
          ) : (overallQuery.data ?? []).length === 0 ? (
            <EmptyState
              icon="podium-outline"
              title={t('predictions.emptyBoardTitle')}
              body={t('predictions.emptyBoardBody')}
            />
          ) : (
            <BoardRows entries={overallQuery.data ?? []} myUserId={userId} />
          )
        ) : openLeagueId ? (
          <View className="gap-4">
            {openLeague ? (
              <Card className="mx-4 gap-2 px-4 py-4">
                <AppText variant="heading">{openLeague.name}</AppText>
                <Pressable
                  accessibilityRole="button"
                  onPress={() => {
                    Clipboard.setStringAsync(openLeague.invite_code).catch(() => {
                      // Code stays visible to copy manually.
                    });
                  }}
                >
                  <AppText variant="caption" weight="bold" tabular className="text-volt-bright">
                    {t('fantasy.leaderboards.inviteCode')}: {openLeague.invite_code}
                  </AppText>
                </Pressable>
              </Card>
            ) : null}
            {leagueBoard.isPending ? (
              <View className="gap-3 px-4">
                <Skeleton className="h-14 w-full" />
              </View>
            ) : leagueBoard.isError ? (
              <ErrorState error={leagueBoard.error} onRetry={() => leagueBoard.refetch()} />
            ) : (
              <BoardRows entries={leagueBoard.data ?? []} myUserId={userId} />
            )}
            <View className="mx-4 gap-3">
              <Button
                label={
                  confirmLeave
                    ? t('fantasy.leaderboards.leaveConfirm')
                    : t('fantasy.leaderboards.leave')
                }
                variant="danger"
                compact
                loading={leaveLeague.isPending}
                onPress={() => {
                  if (!confirmLeave) {
                    setConfirmLeave(true);
                    setTimeout(() => setConfirmLeave(false), 5000);
                    return;
                  }
                  leaveLeague.mutate(openLeagueId, {
                    onSuccess: () => setOpenLeagueId(null),
                  });
                }}
              />
              <Button
                label={t('common.back')}
                variant="ghost"
                compact
                onPress={() => setOpenLeagueId(null)}
              />
            </View>
          </View>
        ) : (
          <View className="gap-4">
            <Card className="mx-4 gap-3 px-4 py-4">
              <AppText variant="heading">{t('predictions.createGroup')}</AppText>
              <TextField
                placeholder={t('predictions.groupNamePlaceholder')}
                value={groupName}
                onChangeText={setGroupName}
                maxLength={30}
              />
              <Button
                label={t('predictions.createGroup')}
                compact
                loading={createLeague.isPending}
                disabled={groupName.trim().length < 3}
                onPress={onCreate}
              />
              {createdCode ? (
                <Pressable
                  accessibilityRole="button"
                  onPress={() => {
                    Clipboard.setStringAsync(createdCode).catch(() => {
                      // Code stays visible to copy manually.
                    });
                  }}
                  className="rounded-xl bg-volt/10 px-3 py-2.5"
                >
                  <AppText variant="caption" weight="bold" tabular className="text-volt-bright">
                    {t('fantasy.leaderboards.inviteCodeShare', { code: createdCode })}
                  </AppText>
                </Pressable>
              ) : null}
            </Card>

            <Card className="mx-4 gap-3 px-4 py-4">
              <AppText variant="heading">{t('predictions.joinGroup')}</AppText>
              <TextField
                placeholder={t('fantasy.leaderboards.inviteCodePlaceholder')}
                value={inviteCode}
                onChangeText={setInviteCode}
                autoCapitalize="characters"
                autoCorrect={false}
                maxLength={12}
              />
              <Button
                label={t('predictions.joinGroup')}
                compact
                loading={joinLeague.isPending}
                disabled={inviteCode.trim().length !== 12}
                onPress={onJoin}
              />
            </Card>

            {formMessage ? (
              <AppText variant="caption" className="px-6 text-loss">
                {formMessage}
              </AppText>
            ) : null}

            {leaguesQuery.isPending ? (
              <View className="gap-3 px-4">
                <Skeleton className="h-14 w-full" />
              </View>
            ) : leaguesQuery.isError ? (
              <ErrorState error={leaguesQuery.error} onRetry={() => leaguesQuery.refetch()} />
            ) : (leaguesQuery.data ?? []).length === 0 ? (
              <EmptyState
                icon="people-outline"
                title={t('predictions.noGroupsTitle')}
                body={t('predictions.noGroupsBody')}
              />
            ) : (
              <Card className="mx-4 overflow-hidden">
                {(leaguesQuery.data ?? []).map((league, index) => (
                  <Pressable
                    key={league.id}
                    accessibilityRole="button"
                    onPress={() => setOpenLeagueId(league.id)}
                    className={`flex-row items-center justify-between px-4 py-3.5 active:bg-surface-2/60 ${
                      index > 0 ? 'border-t border-line' : ''
                    }`}
                  >
                    <AppText variant="caption" weight="semibold" className="text-ink">
                      {league.name}
                    </AppText>
                    <AppText variant="micro" tabular className="text-muted">
                      {league.invite_code}
                    </AppText>
                  </Pressable>
                ))}
              </Card>
            )}
          </View>
        )}
      </ScrollView>
    </Screen>
  );
}
